import { createAsyncThunk } from '@reduxjs/toolkit';
import { ticketsApi } from '../../../api/services/tickets.service';
import {
  TicketFilters,
  TicketListItem,
  TicketDetail,
  TicketCreatePayload,
  TicketOperatorCreatePayload,
  TicketUpdatePayload,
  MessageCreatePayload,
} from '../../../types/ticketInterfaces';
import type { TicketMessage } from '../../../types/ticketInterfaces';

/* ── Список тикетов (с фильтрами и пагинацией) ── */
export const fetchTickets = createAsyncThunk<
  { results: TicketListItem[]; count: number },
  { filters: Partial<TicketFilters>; page: number; limit?: number },
  { rejectValue: string }
>('tickets/fetchTickets', async ({ filters, page, limit = 100 }, thunkApi) => {
  try {
    const response = await ticketsApi.getTickets(filters, page, limit);
    return { results: response.data.results, count: response.data.count };
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка загрузки заявок',
    );
  }
});

/* ── Детали тикета ── */
export const fetchTicketById = createAsyncThunk<
  TicketDetail,
  number,
  { rejectValue: string }
>('tickets/fetchTicketById', async (id, thunkApi) => {
  try {
    const response = await ticketsApi.getTicketById(id);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка загрузки заявки',
    );
  }
});

/* ── Создание тикета ── */
export const createTicket = createAsyncThunk<
  TicketDetail,
  { data: TicketCreatePayload; files?: File[] },
  { rejectValue: string }
>('tickets/createTicket', async ({ data, files }, thunkApi) => {
  try {
    const response = await ticketsApi.createTicket(data, files);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка создания заявки',
    );
  }
});

/* ── Создание тикета оператором ── */
export const createTicketOperator = createAsyncThunk<
  TicketDetail,
  TicketOperatorCreatePayload,
  { rejectValue: string }
>('tickets/createTicketOperator', async (data, thunkApi) => {
  try {
    const response = await ticketsApi.createTicketOperator(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка создания заявки',
    );
  }
});

/* ── Обновление тикета (PATCH) ── */
export const updateTicket = createAsyncThunk<
  TicketDetail,
  { id: number; data: TicketUpdatePayload },
  { rejectValue: string }
>('tickets/updateTicket', async ({ id, data }, thunkApi) => {
  try {
    const response = await ticketsApi.updateTicket(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка обновления заявки',
    );
  }
});

/* ── Решить заявку (POST solve/) ── */
export const solveTicket = createAsyncThunk<
  TicketDetail,
  number,
  { rejectValue: string }
>('tickets/solveTicket', async (id, thunkApi) => {
  try {
    const response = await ticketsApi.solveTicket(id);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка решения заявки',
    );
  }
});

/* ── Закрыть заявку (POST close/) ── */
export const closeTicket = createAsyncThunk<
  TicketDetail,
  number,
  { rejectValue: string }
>('tickets/closeTicket', async (id, thunkApi) => {
  try {
    const response = await ticketsApi.closeTicket(id);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка закрытия заявки',
    );
  }
});

/* ── Назначить оператора (POST assign/) ── */
export const assignTicket = createAsyncThunk<
  TicketDetail,
  { ticketId: number; operatorId: number },
  { rejectValue: string }
>('tickets/assignTicket', async ({ ticketId, operatorId }, thunkApi) => {
  try {
    const response = await ticketsApi.assignTicket(ticketId, operatorId);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка назначения',
    );
  }
});

/* ── Отправить сообщение ── */
export const addTicketMessage = createAsyncThunk<
  TicketMessage,
  { ticketId: number; data: MessageCreatePayload },
  { rejectValue: string }
>('tickets/addMessage', async ({ ticketId, data }, thunkApi) => {
  try {
    const response = await ticketsApi.addMessage(ticketId, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.detail || 'Ошибка отправки сообщения',
    );
  }
});

/* ── Отправить сообщение с файлами ── */
export const addTicketMessageWithFiles = createAsyncThunk<
  TicketMessage,
  { ticketId: number; body: string; files: File[]; isInternal?: boolean },
  { rejectValue: string }
>(
  'tickets/addMessageWithFiles',
  async ({ ticketId, body, files, isInternal }, thunkApi) => {
    try {
      const response = await ticketsApi.addMessageWithFiles(
        ticketId,
        body,
        files,
        isInternal,
      );
      return response.data;
    } catch (e: any) {
      return thunkApi.rejectWithValue(
        e.response?.data?.detail || 'Ошибка отправки сообщения',
      );
    }
  },
);
